import { UI } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import * as NB from 'native-base';
import { IStackProps } from 'native-base';
import React, { useEffect } from 'react';
import { useRecoilState } from 'recoil';

import { FiltersToApplyProps, useFilterTransformer } from '../../../../hooks/useFilterTransformer';
import { NormalizedDistributionProduct } from '../../../../store/managementAnalysis/types';
import { managementAnalysisFiltersAtom } from '../../../../store/managementAnalysisDetails/filters';

import { FilterButton } from './FilterButton';
import { PaidCustodyExplanation } from './PaidCustodyExplanation';
import { validateIfProductIncludesPaidCustody } from './PaidCustodyExplanation/constants';
import { ProductTable } from './ProductTable';

export declare namespace ProductSearchTypes {
  interface Props extends IStackProps {
    testId: string;
    isMobile: boolean;
    isTablet: boolean;
    product: NormalizedDistributionProduct;
  }
}

export const ProductSearch = ({
  testId,
  isMobile,
  isTablet,
  product,
  ...otherProps
}: ProductSearchTypes.Props) => {
  const productIncludesPaidCustody = validateIfProductIncludesPaidCustody(product);

  const [isLoading, setIsLoading] = React.useState(false);
  const [dataLength, setDataLength] = React.useState(0);
  const [searchTerm, setSearchTerm] = React.useState<string | undefined>(undefined);
  const [filtersToApply, setFiltersToApply] = React.useState<FiltersToApplyProps>({});
  const [{ appliedFilters, availableFilters }] = useRecoilState(managementAnalysisFiltersAtom);

  const { transformFilters } = useFilterTransformer(availableFilters);

  const handleSearchTerm = (currentSearchTerm: string) => {
    setSearchTerm(currentSearchTerm.trim().length === 0 ? undefined : currentSearchTerm.trim());
  };

  const handleClear = () => {
    setSearchTerm(undefined);
  };

  const getResultQuantity = () => {
    const quantity = `${dataLength} resultado`;
    if (dataLength === 1) {
      return quantity;
    }
    return `${quantity}s`;
  };

  useEffect(() => {
    setFiltersToApply(transformFilters(appliedFilters, { transformDateFilter: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters]);

  return (
    <NB.HStack testID={testId} {...otherProps}>
      <NB.VStack w={'full'} space={Spacing['inline.xxxs']}>
        {!!isMobile && <UI.Text variant="semibold">BUSCAR POR PRODUTO</UI.Text>}

        <NB.VStack w={'full'} space={Spacing['stack.xxs']}>
          <NB.HStack flex={1} justifyContent="space-between" minH="42px">
            <UI.SearchInput
              isMobile={isMobile}
              isLoading={isLoading}
              onClear={handleClear}
              onSearch={handleSearchTerm}
              hStackProps={isTablet ? { width: '50%' } : {}}
            />
            <NB.HStack space={Spacing['inline.xxs']} alignItems="center">
              {productIncludesPaidCustody && !isMobile && !isTablet && <PaidCustodyExplanation />}
              {!isMobile && <FilterButton isMobile={isMobile} />}
            </NB.HStack>
          </NB.HStack>
          {productIncludesPaidCustody && !isMobile && isTablet && (
            <PaidCustodyExplanation buttonProps={{ alignSelf: 'end' }} />
          )}
          {!!dataLength && !!isMobile && !!searchTerm && (
            <NB.HStack justifyContent="space-between" alignItems="center">
              <UI.Text variant="semibold">Resultado da busca</UI.Text>
              <UI.Text fontSize={Typography['fontsize.xs']}>{getResultQuantity()}</UI.Text>
            </NB.HStack>
          )}
          <ProductTable
            dataLength={dataLength}
            product={product}
            searchTerm={searchTerm}
            filtersToApply={filtersToApply}
            setDataLength={setDataLength}
            setIsLoading={setIsLoading}
          />
        </NB.VStack>
      </NB.VStack>
    </NB.HStack>
  );
};
